import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import {
  Building2,
  ChevronRight,
  ClipboardCheck,
  LayoutList,
  LogOut,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Plug,
  Sun,
  Users,
} from "lucide-react"
import * as React from "react"

import { Avatar } from "@/components/domain/avatar"
import { env } from "@/config/env"
import {
  canListClients,
  canManageUsersAndKeys,
  canUseDevelopment,
  isClient,
  type Role as RoleT,
} from "@/lib/roles"
import { cn } from "@/lib/utils"

/*
   Role-aware navigation: permission is not an error condition; an item a
   role cannot use should not be in the list.

   Three groups, not two — reviewers reaching for Doc Compare are doing
   casework, not administration, and dev tooling is neither. The Development
   group is collapsible + compact: reachable but quiet, so the primary
   workflow surfaces (Work, Admin) breathe.

   Collapses to a 56px rail for the workspace so 248px of navigation does
   not compete with a PDF.
*/

// NavRole is the sidebar's view of the RBAC vocabulary — a plain re-export so both
// the shell and API layers agree on role names via one constant module (lib/roles).
export type NavRole = RoleT

/** Your own account, opened from the name at the foot of the rail. */
export const PROFILE_HREF = "/profile"

export interface NavItemDef {
  id: string
  label: string
  /** One line. Shown only when the sidebar is expanded and the group is not compact. */
  hint?: string
  icon: React.ReactNode
  href: string
  /** Other paths that open the same page, matched exactly (not as a prefix). The queue
   *  lives at `/` and at `/reviews`; its item must light up on both. */
  aliases?: readonly string[]
  /** Roles that may see it. Omit for everyone. */
  roles?: NavRole[]
  /** A capability predicate from lib/roles — the same one the route guards with, so the two
   *  cannot disagree. Checked as well as `roles`. */
  allow?: (role: NavRole) => boolean
  badge?: number | undefined
}

export interface NavGroupDef {
  id: string
  label: string
  items: NavItemDef[]
  roles?: NavRole[]
  /** A capability predicate for the whole group, from lib/roles — the one its routes guard
   *  with. Checked as well as `roles`. */
  allow?: (role: NavRole) => boolean
  /** Collapsed by default. For secondary/deprecated surfaces we still want reachable. */
  collapsible?: boolean
  /** Suppress inline hints; still surface on hover via tooltip. Keeps deprecated groups quiet. */
  compact?: boolean
  /** A collapsible group that starts open. */
  defaultOpen?: boolean
}

/** Development tooling, admins only. Left out of `NAV` on production — see there. */
const ADVANCED_GROUP: NavGroupDef = {
  id: "advanced",
  label: "Development",
  allow: canUseDevelopment,
  collapsible: true,
  compact: true,
  // Open: Evaluation runs and Integrations are in daily use, not tucked-away tooling.
  defaultOpen: true,
  items: [
    // Rules, the EPA Label Finder, Doc Compare, Field Review and the dataset browser are
    // hidden until they are built on this console; their routes stay behind the same flag.
    {
      id: "evaluations",
      label: "Evaluation runs",
      hint: "The review measured against expert-marked labels",
      icon: <ClipboardCheck />,
      href: "/evaluations",
    },
    {
      id: "integration",
      label: "Integrations",
      hint: "Keys and API documentation",
      icon: <Plug />,
      href: "/dev/integrations",
    },
  ],
}

/** The default map. Passed in rather than imported so tests can vary it. */
export const NAV: NavGroupDef[] = [
  {
    id: "work",
    label: "Work",
    items: [
      {
        id: "queue",
        label: "Review queue",
        hint: "Labels in review",
        icon: <LayoutList />,
        href: "/reviews",
        aliases: ["/"],
      },
    ],
  },
  // Each item is gated by the predicate its route's RequireRole uses (app/router.tsx), so a
  // link is shown exactly when the page will open. The group drops out when both are hidden.
  {
    id: "admin",
    label: "Admin",
    items: [
      {
        id: "clients",
        label: "Clients",
        hint: "Engagements",
        icon: <Building2 />,
        href: "/admin/clients",
        allow: canListClients,
      },
      {
        id: "users",
        label: "Users",
        hint: "Console accounts",
        icon: <Users />,
        href: "/admin/users",
        allow: canManageUsersAndKeys,
      },
    ],
  },
  // Development tooling: every environment except production (see `env.showDevTools`).
  // The router gates the same pages on the same flag, so production has neither the
  // links nor the routes.
  ...(env.showDevTools ? [ADVANCED_GROUP] : []),
]

/** Clients get their own map — not a filtered version of the internal one. */
export const CLIENT_NAV: NavGroupDef[] = [
  {
    id: "work",
    label: "Your reviews",
    items: [
      {
        id: "queue",
        label: "Label reviews",
        hint: "Labels we are reviewing",
        icon: <LayoutList />,
        href: "/reviews",
        aliases: ["/"],
      },
    ],
  },
]

/**
 * The id of the nav item a path belongs to, or "" when none does. An item owns its href, any
 * path under it (`/reviews/42` is the queue's), and its aliases exactly. The longest href wins,
 * so a nested item beats its parent.
 */
export function activeNavId(pathname: string, nav: readonly NavGroupDef[]): string {
  const owns = (item: NavItemDef) =>
    pathname === item.href ||
    pathname.startsWith(`${item.href}/`) ||
    (item.aliases?.includes(pathname) ?? false)
  return (
    nav
      .flatMap((g) => g.items)
      .filter(owns)
      .sort((a, b) => b.href.length - a.href.length)[0]?.id ?? ""
  )
}

/** The nav map a role signs in to: a client gets its own, everyone else the internal one. */
export function navFor(role: NavRole): NavGroupDef[] {
  return isClient(role) ? CLIENT_NAV : NAV
}

export function visibleNav(nav: NavGroupDef[], role: NavRole): NavGroupDef[] {
  return nav
    .filter((g) => (!g.roles || g.roles.includes(role)) && (!g.allow || g.allow(role)))
    .map((g) => ({
      ...g,
      items: g.items.filter(
        (i) => (!i.roles || i.roles.includes(role)) && (!i.allow || i.allow(role)),
      ),
    }))
    .filter((g) => g.items.length > 0)
}

export interface SidebarProps {
  nav: NavGroupDef[]
  role: NavRole
  activeId: string
  collapsed: boolean
  onCollapsedChange: (c: boolean) => void
  user: { name: string; email: string; roleLabel: string }
  theme: "light" | "dark"
  onThemeChange: (t: "light" | "dark") => void
  // `| undefined` as well as `?`: under exactOptionalPropertyTypes a caller
  // spreading an absent handler passes an explicit undefined, which `?` alone
  // rejects.
  onNavigate?: ((href: string) => void) | undefined
  onSignOut?: (() => void) | undefined
}

export function Sidebar({
  nav,
  role,
  activeId,
  collapsed,
  onCollapsedChange,
  user,
  theme,
  onThemeChange,
  onNavigate,
  onSignOut,
}: SidebarProps) {
  const groups = React.useMemo(() => visibleNav(nav, role), [nav, role])

  const [openGroups, setOpenGroups] = React.useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {}
    for (const g of groups) {
      if (g.collapsible) {
        initial[g.id] = g.defaultOpen === true || g.items.some((i) => i.id === activeId)
      }
    }
    return initial
  })

  React.useEffect(() => {
    setOpenGroups((prev) => {
      const next = { ...prev }
      for (const g of groups) {
        if (g.collapsible && g.items.some((i) => i.id === activeId)) {
          next[g.id] = true
        }
      }
      return next
    })
  }, [activeId, groups])

  return (
    <nav
      aria-label="Main"
      data-collapsed={collapsed || undefined}
      className={cn(
        "flex h-full shrink-0 flex-col border-r border-border bg-surface",
        "transition-[width] duration-260 ease-out motion-reduce:transition-none",
        collapsed ? "w-14" : "w-[248px]",
      )}
    >
      <div
        className={cn(
          "relative flex shrink-0 items-center overflow-hidden border-b border-border",
          "bg-brand-tint/50",
          collapsed ? "h-14 justify-center px-0" : "h-[76px] px-4",
        )}
      >
        {!collapsed && (
          <img
            src="/vela-mark.svg"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute right-3 top-1/2 size-[52px] -translate-y-1/2 opacity-[0.14]"
          />
        )}

        {collapsed ? (
          <img src="/vela-mark.svg" alt="Vela" className="h-7 w-7" />
        ) : (
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <img src="/vela-mark.svg" alt="" aria-hidden="true" className="size-6" />
              <span className="font-id text-[22px] font-medium leading-none tracking-[0.32em] text-brand">
                VELA
              </span>
            </div>
            <div className="mt-1.5 whitespace-nowrap font-id text-[9.5px] uppercase tracking-[0.06em] text-text-3">
              by Pyxis Regulatory Consulting
            </div>
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto py-3">
        {groups.map((g) => {
          const isCollapsible = Boolean(g.collapsible) && !collapsed
          const isOpen = isCollapsible ? Boolean(openGroups[g.id]) : true
          const panelId = `nav-group-${g.id}`

          return (
            <div key={g.id} className="mb-3 last:mb-0">
              {collapsed ? (
                <div className="mx-3 mb-2 h-px bg-border" aria-hidden="true" />
              ) : isCollapsible ? (
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => setOpenGroups((prev) => ({ ...prev, [g.id]: !isOpen }))}
                  className="group/label flex w-full items-center gap-2 px-5 pb-2 pt-1 text-left"
                >
                  <ChevronRight
                    aria-hidden="true"
                    className={cn(
                      "size-3 text-text-3 transition-transform duration-180 ease-out",
                      "motion-reduce:transition-none",
                      isOpen && "rotate-90",
                    )}
                  />
                  <span className="font-id text-caption uppercase tracking-[0.16em] text-text-3 group-hover/label:text-text-2">
                    {g.label}
                  </span>
                  <span aria-hidden="true" className="h-px flex-1 bg-border" />
                </button>
              ) : (
                <div className="flex items-center gap-2 px-5 pb-2 pt-1">
                  <span className="font-id text-caption uppercase tracking-[0.16em] text-text-3">
                    {g.label}
                  </span>
                  <span aria-hidden="true" className="h-px flex-1 bg-border" />
                </div>
              )}
              {isOpen && (
                <ul id={panelId} className="space-y-0.5">
                  {g.items.map((item) => (
                    <li key={item.id}>
                      <NavItem
                        item={item}
                        active={activeId === item.id}
                        collapsed={collapsed}
                        compact={Boolean(g.compact)}
                        onNavigate={onNavigate}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )
        })}
      </div>

      <div className="shrink-0 border-t border-border p-2.5">
        <div
          className={cn("flex items-center gap-2.5", collapsed ? "justify-center" : "px-1.5 py-1")}
        >
          <a
            href={PROFILE_HREF}
            aria-label={collapsed ? "Your account" : undefined}
            title="Your account"
            onClick={(e) => {
              if (onNavigate) {
                e.preventDefault()
                onNavigate(PROFILE_HREF)
              }
            }}
            className={cn(
              "flex min-w-0 items-center gap-2.5 rounded-lg no-underline",
              "transition-colors duration-120 ease-out hover:bg-surface-sunken",
              "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
              collapsed ? "p-0.5" : "-m-1 flex-1 p-1",
            )}
          >
            <Avatar name={user.name} identity={user.email} size={collapsed ? "sm" : "md"} />
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <div className="truncate text-body font-medium text-text">{user.name}</div>
                <div className="truncate font-id text-caption text-text-3">{user.roleLabel}</div>
              </div>
            )}
          </a>
          {!collapsed && (
            <>
              {onSignOut && (
                <RailButton
                  label="Sign out"
                  icon={<LogOut />}
                  collapsed={false}
                  onClick={onSignOut}
                  tone="danger"
                />
              )}
            </>
          )}
        </div>

        <div
          className={cn(
            "mt-2.5 border-t border-border-soft pt-2.5 flex gap-1.5",
            collapsed ? "flex-col items-center" : "items-center justify-between px-1",
          )}
        >
          <RailButton
            label={theme === "dark" ? "Switch to light" : "Switch to dark"}
            icon={theme === "dark" ? <Sun /> : <Moon />}
            collapsed={collapsed}
            onClick={() => {
              onThemeChange(theme === "dark" ? "light" : "dark")
            }}
            bordered
          />
          <RailButton
            label={collapsed ? "Expand the sidebar" : "Collapse to a rail"}
            icon={collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
            collapsed={collapsed}
            onClick={() => {
              onCollapsedChange(!collapsed)
            }}
            bordered
          />
        </div>
      </div>
    </nav>
  )
}

export function NavItem({
  item,
  active,
  collapsed,
  compact = false,
  onNavigate,
}: {
  item: NavItemDef
  active: boolean
  collapsed: boolean
  compact?: boolean | undefined
  onNavigate?: ((href: string) => void) | undefined
}) {
  const body = (
    <a
      href={item.href}
      aria-current={active ? "page" : undefined}
      onClick={(e) => {
        if (onNavigate) {
          e.preventDefault()
          onNavigate(item.href)
        }
      }}
      className={cn(
        "group relative flex gap-3 no-underline",
        "transition-colors duration-120 ease-out",
        collapsed
          ? "mx-2 h-9 items-center justify-center rounded-md"
          : "items-center px-3 py-2 mx-2 rounded-lg",
        active ? "bg-brand-tint text-brand" : "text-text-2 hover:bg-surface-sunken hover:text-text",
      )}
    >
      <span
        className={cn(
          "relative shrink-0 grid place-items-center rounded-md size-8 [&_svg]:size-5",
          "transition-colors duration-120 ease-out",
          active
            ? "bg-brand text-white shadow-[0_1px_2px_color-mix(in_srgb,var(--brand)_25%,transparent)]"
            : // A hairline on the page's own surface, matching the theme and
              // collapse buttons at the foot of this rail. A filled tile on every
              // inactive item makes the rail a column of blocks with the active
              // one lost among them, and leaves hover with nowhere to go.
              //
              // `--text-3` is the caption floor, set for text that may be
              // skipped. A navigation icon is a target, and in the collapsed rail
              // it is the only thing naming the destination, so it reads at body
              // weight.
              "border border-border bg-surface text-text-2 group-hover:border-border-strong group-hover:bg-surface-sunken group-hover:text-text",
        )}
      >
        {item.icon}
        {collapsed && item.badge ? (
          <span
            className={cn(
              "absolute -right-1 -top-1 min-w-[16px] h-4 grid place-items-center px-1",
              "rounded-full bg-brand font-id text-[10px] font-semibold leading-none text-white",
              "ring-2 ring-[color:var(--surface)]",
            )}
          >
            {item.badge}
          </span>
        ) : null}
      </span>

      {!collapsed && (
        <span className="min-w-0 flex-1">
          <span className="block truncate text-body font-medium">{item.label}</span>
          {item.hint && !compact && (
            <span className="block text-body-sm font-normal leading-snug text-text-3">
              {item.hint}
            </span>
          )}
        </span>
      )}

      {!collapsed && item.badge ? (
        <span
          className={cn(
            "shrink-0 self-start mt-0.5 min-w-[20px] h-5 grid place-items-center px-1.5",
            "rounded-full font-id text-[11px] font-semibold leading-none",
            "bg-brand text-white",
          )}
        >
          {item.badge}
        </span>
      ) : null}
    </a>
  )

  return (
    <TooltipPrimitive.Root delayDuration={collapsed ? 200 : 400}>
      <TooltipPrimitive.Trigger asChild>{body}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side="right"
          sideOffset={8}
          className="z-toast max-w-[240px] rounded-md bg-text px-2.5 py-1.5 text-caption text-[color:var(--surface)] shadow-e3"
        >
          <div className="font-medium">{item.label}</div>
          {item.hint && <div className="mt-0.5 opacity-70">{item.hint}</div>}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
}

function RailButton({
  label,
  icon,
  collapsed,
  onClick,
  bordered = false,
  tone = "neutral",
}: {
  label: string
  icon: React.ReactNode
  collapsed: boolean
  onClick: () => void
  bordered?: boolean
  tone?: "neutral" | "danger"
}) {
  return (
    <TooltipPrimitive.Root delayDuration={300}>
      <TooltipPrimitive.Trigger asChild>
        <button
          type="button"
          aria-label={label}
          onClick={onClick}
          className={cn(
            "grid size-8 place-items-center rounded-md",
            "transition-colors duration-120 ease-out",
            "[&_svg]:size-4.5",
            bordered
              ? "border border-border bg-surface text-text-2 hover:bg-surface-sunken hover:border-border-strong hover:text-text"
              : "text-text-2 hover:bg-surface-sunken hover:text-text",
            tone === "danger" && "hover:bg-err-tint hover:text-err hover:border-err",
          )}
        >
          {icon}
        </button>
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={collapsed ? "right" : "top"}
          sideOffset={6}
          className="z-toast rounded-md bg-text px-2 py-1 text-caption text-[color:var(--surface)] shadow-e3"
        >
          {label}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
}
